import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus,
    Search,
    FileText,
    Package,
    Trash2,
    Calendar,
    Pencil,
    X,
    Save,
    Tag,
    Percent,
    LayoutGrid,
    CheckCircle2,
    ArrowRight
} from 'lucide-react';
import { proveedorService } from '../../services/maintenance/proveedorService';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import toast from 'react-hot-toast';

interface Paquete {
    id: number;
    nombre: string;
    descripcion: string;
    proveedor: {
        id: number;
        razon_social: string;
    };
    precio_paquete: number;
    descuento_porcentaje: number;
    vigencia_desde: string;
    vigencia_hasta: string | null;
    total_items: number;
    activo: boolean;
}

interface ItemCatalogo {
    id: number;
    codigo_interno: string;
    nombre: string;
    categoria: string;
    unidad_medida: string;
    empresa_id?: number;
    en_uso_plan?: boolean;
}

interface Proveedor {
    id: number;
    razon_social: string;
}

const CotizacionPaquete: React.FC = () => {
    const [paquetes, setPaquetes] = useState<Paquete[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);

    // Form Data
    const [itemsCatalogo, setItemsCatalogo] = useState<ItemCatalogo[]>([]);
    const [proveedores, setProveedores] = useState<Proveedor[]>([]);
    const [formLoading, setFormLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

    // Item Search inside Modal
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [filteredCatalogItems, setFilteredCatalogItems] = useState<ItemCatalogo[]>([]);

    // Catalog Explorer State
    const [showCatalogExplorer, setShowCatalogExplorer] = useState(false);
    const [explorerSearch, setExplorerSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('Todos');

    const [formData, setFormData] = useState({
        proveedor_id: '',
        nombre: '',
        descripcion: '',
        precio_paquete: '',
        descuento_porcentaje: '',
        vigencia_desde: new Date().toISOString().split('T')[0],
        vigencia_hasta: '',
        items: [] as Array<{ item_catalogo_id: number; nombre: string; cantidad: number; codigo: string }>
    });

    useEffect(() => {
        loadPaquetes();
    }, []);

    // Filter catalog items
    useEffect(() => {
        if (itemSearchTerm.length > 0) {
            const lower = itemSearchTerm.toLowerCase();
            const filtered = itemsCatalogo.filter(i => {
                // Filtro: Mostrar si es de la empresa O si está en uso en planes
                const isRelevant = i.empresa_id != null || i.en_uso_plan;
                if (!isRelevant) return false;

                return i.nombre.toLowerCase().includes(lower) ||
                    i.codigo_interno.toLowerCase().includes(lower);
            }).slice(0, 8); // Limit suggestions for performance
            setFilteredCatalogItems(filtered);
        } else {
            setFilteredCatalogItems([]);
        }
    }, [itemSearchTerm, itemsCatalogo]);

    // Derived state for categories (Explorer)
    const categories = useMemo(() => {
        // Categorías de ítems relevantes
        const relevantItems = itemsCatalogo.filter(i => i.empresa_id != null || i.en_uso_plan);
        const cats = new Set(relevantItems.map(i => i.categoria).filter(Boolean));
        return ['Todos', ...Array.from(cats)];
    }, [itemsCatalogo]);

    // Filtered items for Explorer
    const explorerItems = useMemo(() => {
        return itemsCatalogo.filter(item => {
            // Filtro: Mostrar si es de la empresa O si está en uso en planes
            const isRelevant = item.empresa_id != null || item.en_uso_plan;
            if (!isRelevant) return false;

            const matchSearch = item.nombre.toLowerCase().includes(explorerSearch.toLowerCase()) ||
                item.codigo_interno.toLowerCase().includes(explorerSearch.toLowerCase());
            const matchCategory = selectedCategory === 'Todos' || item.categoria === selectedCategory;
            return matchSearch && matchCategory;
        });
    }, [itemsCatalogo, explorerSearch, selectedCategory]);

    const loadPaquetes = async () => {
        try {
            setLoading(true);
            const response = await proveedorService.getPaquetes();
            if (response.success) {
                setPaquetes(response.data);
            }
        } catch (error) {
            console.error('Error al cargar paquetes:', error);
            toast.error('Error al cargar lista de paquetes');
        } finally {
            setLoading(false);
        }
    };

    const loadValidData = async () => {
        try {
            setFormLoading(true);
            const [itemsRes, provRes] = await Promise.all([
                maintenanceService.getItemsCatalogo(),
                proveedorService.getProveedores()
            ]);

            if (itemsRes.success) setItemsCatalogo(itemsRes.data);
            if (provRes.success) setProveedores(provRes.data);
            return true;
        } catch (error) {
            console.error('Error al cargar datos del formulario:', error);
            toast.error('Error al cargar datos necesarios');
            setFormLoading(false);
            return false;
        }
    }

    const handleOpenModal = () => {
        setEditingId(null);
        setFormData({
            proveedor_id: '',
            nombre: '',
            descripcion: '',
            precio_paquete: '',
            descuento_porcentaje: '',
            vigencia_desde: new Date().toISOString().split('T')[0],
            vigencia_hasta: '',
            items: []
        });
        setItemSearchTerm('');
        setShowModal(true);
        loadValidData().then(() => setFormLoading(false));
    };

    const handleEdit = async (id: number) => {
        setShowModal(true);
        const success = await loadValidData();
        if (!success) return;

        try {
            const response = await proveedorService.getPaqueteDetalle(id);
            if (response.success) {
                const data = response.data;
                const formatDate = (dateStr: string) => dateStr ? dateStr.split('T')[0] : '';

                // Ensure items structure matches interface
                const mappedItems = data.items.map((i: any) => ({
                    item_catalogo_id: i.item_catalogo_id,
                    nombre: i.nombre || i.item_catalogo?.nombre || 'Ítem desconocido',
                    cantidad: i.cantidad,
                    codigo: i.item_catalogo?.codigo_interno || i.codigo || 'S/C'
                }));

                setFormData({
                    proveedor_id: data.proveedor.id.toString(),
                    nombre: data.nombre,
                    descripcion: data.descripcion || '',
                    precio_paquete: data.precio_paquete,
                    descuento_porcentaje: data.descuento_porcentaje || '',
                    vigencia_desde: formatDate(data.vigencia_desde),
                    vigencia_hasta: formatDate(data.vigencia_hasta),
                    items: mappedItems
                });
                setEditingId(id);
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
            toast.error('Error al cargar información del paquete');
            setShowModal(false);
        } finally {
            setFormLoading(false);
        }
    };

    const handleAddItem = (item: ItemCatalogo) => {
        if (formData.items.some(i => i.item_catalogo_id === item.id)) {
            toast.error('El ítem ya está agregado al paquete');
            return;
        }

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                item_catalogo_id: item.id,
                nombre: item.nombre,
                cantidad: 1,
                codigo: item.codigo_interno
            }]
        }));

        setItemSearchTerm('');
        setFilteredCatalogItems([]);
    };

    const handleUpdateQuantity = (index: number, newQuantity: number) => {
        if (newQuantity < 1) return;
        const updatedItems = [...formData.items];
        updatedItems[index].cantidad = newQuantity;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleRemoveItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.items.length === 0) {
            toast.error('Debes agregar al menos un ítem al paquete');
            return;
        }

        try {
            const payload = {
                ...formData,
                proveedor_id: Number(formData.proveedor_id),
                precio_paquete: parseFloat(formData.precio_paquete),
                descuento_porcentaje: formData.descuento_porcentaje ? parseFloat(formData.descuento_porcentaje) : null,
                vigencia_hasta: formData.vigencia_hasta || null
            };

            if (editingId) {
                await proveedorService.updatePaquete(editingId, payload);
                toast.success('Paquete actualizado exitosamente');
            } else {
                await proveedorService.createPaquete(payload);
                toast.success('Paquete creado exitosamente');
            }

            setShowModal(false);
            setEditingId(null);
            loadPaquetes();
        } catch (error) {
            console.error('Error al guardar paquete:', error);
            toast.error('Error al guardar paquete');
        }
    };

    const handleDelete = (id: number) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteTargetId) return;
        try {
            await proveedorService.deletePaquete(deleteTargetId);
            toast.success('Paquete desactivado');
            loadPaquetes();
            setShowDeleteModal(false);
            setDeleteTargetId(null);
        } catch (error) {
            console.error('Error al eliminar paquete:', error);
            toast.error('Error al eliminar paquete');
        }
    };

    const filteredPaquetes = paquetes.filter(p =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.proveedor.razon_social.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">

            {/* Header Toolbar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Cotización por Paquete</h2>
                    <p className="text-sm text-gray-500">Gestiona precios agrupados de tus proveedores</p>
                </div>

                <div className="flex w-full md:w-auto gap-4 flex-col md:flex-row">
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar paquete o proveedor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        />
                    </div>
                    <button
                        onClick={handleOpenModal}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium whitespace-nowrap"
                    >
                        <Plus size={18} />
                        Nueva Cotización
                    </button>
                </div>
            </div>

            {/* Grid de Paquetes */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
            ) : filteredPaquetes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredPaquetes.map((paquete) => (
                        <div key={paquete.id} className="group bg-white rounded-xl shadow-sm border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200 flex flex-col h-full">
                            {/* Card Header */}
                            <div className="p-5 flex justify-between items-start border-b border-gray-50">
                                <div className="flex items-start gap-3">
                                    <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg shrink-0">
                                        <Package size={24} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-bold text-gray-900 leading-tight truncate pr-2">{paquete.nombre}</h3>
                                        <p className="text-sm text-gray-500 mt-1 truncate">{paquete.proveedor.razon_social}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleEdit(paquete.id)}
                                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                        title="Editar"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(paquete.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Card Body */}
                            <div className="px-5 py-4 flex-1 flex flex-col justify-between">
                                <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                                    <div>
                                        <span className="text-gray-400 block mb-1">Items</span>
                                        <span className="font-semibold text-gray-700 flex items-center gap-1.5">
                                            <Tag size={12} className="text-gray-400" /> {paquete.total_items}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-gray-400 block mb-1">Vigencia</span>
                                        <span className={`font-semibold inline-flex items-center gap-1 ${!paquete.vigencia_hasta || new Date(paquete.vigencia_hasta) >= new Date()
                                            ? 'text-green-600'
                                            : 'text-red-600'
                                            }`}>
                                            <Calendar size={12} />
                                            {paquete.vigencia_hasta ? paquete.vigencia_hasta.split('T')[0] : 'Indefinida'}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex justify-between items-end pt-3 border-t border-gray-100">
                                    <div>
                                        <span className="text-xs text-gray-400 uppercase font-bold tracking-wider">Precio Total</span>
                                        <div className="text-xl font-bold text-gray-900 mt-0.5">
                                            S/ {parseFloat(String(paquete.precio_paquete)).toFixed(2)}
                                        </div>
                                    </div>
                                    {Number(paquete.descuento_porcentaje) > 0 && (
                                        <div className="flex flex-col items-end">
                                            <span className="text-[10px] uppercase font-bold text-gray-400 mb-0.5">Ahorro</span>
                                            <span className="text-xs text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100 flex items-center gap-1">
                                                <Percent size={10} />
                                                {parseFloat(String(paquete.descuento_porcentaje))}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-dashed border-gray-300 p-12 text-center">
                    <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Package className="text-gray-300" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No hay paquetes registrados</h3>
                    <p className="text-gray-500 mt-1 max-w-md mx-auto mb-6">
                        Crea paquetes promocionales o agrupados para simplificar tus compras y obtener mejores precios.
                    </p>
                    <button
                        onClick={handleOpenModal}
                        className="inline-flex items-center gap-2 text-indigo-600 font-medium text-sm hover:text-indigo-800 transition-colors"
                    >
                        <Plus size={16} /> Crear primer paquete
                    </button>
                </div>
            )}

            {/* Modal Formulario */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-hidden animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">

                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {editingId ? 'Editar Cotización' : 'Nueva Cotización de Paquete'}
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">Complete la información del paquete y sus ítems</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-hidden">
                            {formLoading ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-3">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
                                    <span className="text-sm font-medium">Cargando datos...</span>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col md:flex-row">

                                    {/* Sidebar: Información General */}
                                    <div className="w-full md:w-1/3 border-r border-gray-200 p-6 overflow-y-auto bg-gray-50/30">
                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                            <FileText size={14} /> Datos del Paquete
                                        </h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proveedor <span className="text-red-500">*</span></label>
                                                <select
                                                    required
                                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                                                    value={formData.proveedor_id}
                                                    onChange={e => setFormData({ ...formData, proveedor_id: e.target.value })}
                                                >
                                                    <option value="">Seleccione proveedor...</option>
                                                    {proveedores.map(prov => (
                                                        <option key={prov.id} value={prov.id}>{prov.razon_social}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre del Paquete <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="Ej: Kit Mantenimiento 5000km"
                                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                                                    value={formData.nombre}
                                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descripción</label>
                                                <textarea
                                                    rows={3}
                                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow resize-none"
                                                    placeholder="Detalles adicionales del paquete..."
                                                    value={formData.descripcion}
                                                    onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Precio (S/) <span className="text-red-500">*</span></label>
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">S/</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            required
                                                            className="w-full pl-8 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow font-medium text-gray-900"
                                                            value={formData.precio_paquete}
                                                            onChange={e => setFormData({ ...formData, precio_paquete: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descuento (%)</label>
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow text-center"
                                                            value={formData.descuento_porcentaje}
                                                            onChange={e => setFormData({ ...formData, descuento_porcentaje: e.target.value })}
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3 pt-2">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Vigencia Desde</label>
                                                    <input
                                                        type="date"
                                                        required
                                                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                                                        value={formData.vigencia_desde}
                                                        onChange={e => setFormData({ ...formData, vigencia_desde: e.target.value })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Vigencia Hasta</label>
                                                    <input
                                                        type="date"
                                                        className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
                                                        value={formData.vigencia_hasta}
                                                        onChange={e => setFormData({ ...formData, vigencia_hasta: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Main Content: Selección de Ítems */}
                                    <div className="w-full md:w-2/3 p-6 flex flex-col bg-white">
                                        <div className="mb-4">
                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex justify-between items-center">
                                                <span className="flex items-center gap-2"><Package size={14} /> Contenido del Paquete</span>
                                                <span className="bg-indigo-50 text-indigo-700 py-0.5 px-2 rounded-full text-[10px] font-bold">
                                                    {formData.items.length} ítems
                                                </span>
                                            </h4>

                                            {/* Buscador Integrado */}
                                            <div className="relative group">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                <input
                                                    type="text"
                                                    placeholder="Buscar repuesto, servicio o código para agregar..."
                                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white outline-none transition-all shadow-sm"
                                                    value={itemSearchTerm}
                                                    onChange={e => setItemSearchTerm(e.target.value)}
                                                />
                                                {/* Suggestions Dropdown */}
                                                {itemSearchTerm && filteredCatalogItems.length > 0 && (
                                                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                                                        {filteredCatalogItems.map(item => (
                                                            <button
                                                                type="button"
                                                                key={item.id}
                                                                onClick={() => handleAddItem(item)}
                                                                className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors flex justify-between items-center group/item"
                                                            >
                                                                <div>
                                                                    <span className="font-medium text-gray-800 text-sm group-hover/item:text-indigo-700 block">{item.nombre}</span>
                                                                    <span className="text-xs text-gray-400 font-mono">{item.codigo_interno}</span>
                                                                </div>
                                                                <div className="bg-indigo-100 text-indigo-600 p-1.5 rounded-lg opacity-0 group-hover/item:opacity-100 transition-opacity">
                                                                    <Plus size={14} strokeWidth={3} />
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Botón Explorar Catálogo */}
                                        <div className="mt-3 mb-2 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowCatalogExplorer(true);
                                                    setExplorerSearch('');
                                                    setSelectedCategory('Todos');
                                                }}
                                                className="flex items-center gap-2 text-indigo-600 font-medium text-xs hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors border border-transparent hover:border-indigo-100"
                                            >
                                                <LayoutGrid size={16} />
                                                Explorar Catálogo Completo
                                            </button>
                                        </div>

                                        {/* Table of Items */}
                                        <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden flex flex-col bg-gray-50/30">
                                            <div className="overflow-y-auto flex-1 p-0">
                                                {formData.items.length > 0 ? (
                                                    <table className="w-full text-sm text-left border-collapse">
                                                        <thead className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider sticky top-0 z-10 shadow-sm">
                                                            <tr>
                                                                <th className="px-4 py-3 w-20 border-b border-gray-200">Código</th>
                                                                <th className="px-4 py-3 border-b border-gray-200">Descripción</th>
                                                                <th className="px-4 py-3 w-24 text-center border-b border-gray-200">Cant.</th>
                                                                <th className="px-4 py-3 w-10 border-b border-gray-200"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100 bg-white">
                                                            {formData.items.map((item, index) => (
                                                                <tr key={index} className="hover:bg-gray-50 transition-colors group">
                                                                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.codigo}</td>
                                                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                                                    <td className="px-4 py-2">
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={item.cantidad}
                                                                            onChange={(e) => handleUpdateQuantity(index, parseInt(e.target.value))}
                                                                            className="w-16 mx-auto block text-center bg-gray-50 border border-gray-200 rounded py-1 px-1 focus:ring-2 focus:ring-indigo-500 focus:bg-white text-sm font-semibold text-gray-700 transition-all outline-none"
                                                                        />
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveItem(index)}
                                                                            className="text-gray-300 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded-md"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                                                        <div className="bg-white p-4 rounded-full shadow-sm border border-gray-100">
                                                            <Package size={40} className="text-gray-200" />
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-sm font-medium text-gray-500">Tu lista está vacía</p>
                                                            <p className="text-xs text-gray-400 mt-1">Usa el buscador para agregar ítems</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Footer con totales simple si se necesitara */}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Catalog Explorer Modal (Layered) */}
                        {showCatalogExplorer && (
                            <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[70] flex items-center justify-center p-4">
                                <div className="bg-white w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden border border-gray-100">
                                    {/* Header Explorer */}
                                    <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10 shadow-sm">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">Catálogo de Repuestos y Servicios</h3>
                                            <p className="text-sm text-gray-500">Selecciona los ítems para tu paquete</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowCatalogExplorer(false)}
                                            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                                        >
                                            <X size={24} />
                                        </button>
                                    </div>

                                    <div className="flex flex-1 overflow-hidden">
                                        {/* Sidebar Categorias */}
                                        <div className="w-64 border-r border-gray-200 bg-gray-50/50 p-4 overflow-y-auto hidden md:block">
                                            <h4 className="font-semibold text-xs text-gray-500 uppercase tracking-wider mb-3">Categorías</h4>
                                            <div className="space-y-1">
                                                {categories.map(cat => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        onClick={() => setSelectedCategory(cat)}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex justify-between items-center ${selectedCategory === cat
                                                            ? 'bg-indigo-50 text-indigo-700'
                                                            : 'text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        <span className="truncate">{cat}</span>
                                                        {selectedCategory === cat && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Main Content Explorer */}
                                        <div className="flex-1 flex flex-col bg-white">
                                            {/* Search Bar inside Explorer */}
                                            <div className="p-4 border-b border-gray-100 flex gap-4 items-center">
                                                <div className="relative flex-1">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar en el catálogo..."
                                                        value={explorerSearch}
                                                        onChange={(e) => setExplorerSearch(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                    />
                                                </div>
                                            </div>

                                            {/* Items Grid */}
                                            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/30">
                                                {explorerItems.length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {explorerItems.map(item => {
                                                            const isSelected = formData.items.some(si => si.item_catalogo_id === item.id);
                                                            return (
                                                                <div
                                                                    key={item.id}
                                                                    onClick={() => {
                                                                        if (isSelected) {
                                                                            const index = formData.items.findIndex(i => i.item_catalogo_id === item.id);
                                                                            if (index > -1) handleRemoveItem(index);
                                                                        } else {
                                                                            handleAddItem(item);
                                                                        }
                                                                    }}
                                                                    className={`border rounded-xl p-4 transition-all cursor-pointer relative group ${isSelected
                                                                        ? 'border-indigo-200 bg-indigo-50/40 ring-1 ring-indigo-200'
                                                                        : 'border-gray-200 hover:border-indigo-300 hover:shadow-md bg-white'
                                                                        }`}
                                                                >
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div className={`p-2 rounded-lg transition-colors ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500 group-hover:text-indigo-600'}`}>
                                                                            <Package size={20} />
                                                                        </div>
                                                                        {isSelected ? (
                                                                            <span className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-white border border-indigo-100 px-2 py-0.5 rounded-full shadow-sm">
                                                                                <CheckCircle2 size={10} /> En Paquete
                                                                            </span>
                                                                        ) : (
                                                                            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400">
                                                                                <Plus size={20} />
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <h5 className="font-bold text-gray-900 text-sm leading-tight mb-1 line-clamp-2" title={item.nombre}>
                                                                        {item.nombre}
                                                                    </h5>
                                                                    <p className="text-xs text-mono text-gray-500 mb-3">{item.codigo_interno}</p>

                                                                    <div className="flex justify-between items-center text-xs pt-3 border-t border-gray-100 border-dashed mt-auto">
                                                                        <span className="font-medium text-gray-400 uppercase">{item.categoria}</span>
                                                                        <span className="font-bold text-gray-600">{item.unidad_medida}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                                                        <Package size={48} className="mb-4 opacity-20" />
                                                        <p>No se encontraron ítems</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer Explorer */}
                                            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                                                <span className="text-xs font-medium text-gray-500">
                                                    {formData.items.length} ítems seleccionados
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCatalogExplorer(false)}
                                                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
                                                >
                                                    Volver a la Cotización <ArrowRight size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 z-10">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2.5 text-gray-700 font-medium hover:bg-white hover:border-gray-300 hover:shadow-sm rounded-lg transition-all border border-transparent text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-2 text-sm"
                            >
                                <Save size={18} />
                                {editingId ? 'Guardar Cambios' : 'Crear Cotización'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden scale-100 transform transition-all">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Trash2 className="text-red-500" size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">¿Desactivar Paquete?</h3>
                            <p className="text-gray-500 text-sm mb-6">Esta acción desactivará el paquete y no será visible para nuevas cotizaciones. ¿Deseas continuar?</p>

                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-4 py-2 text-gray-600 font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 text-white font-medium bg-red-600 hover:bg-red-700 rounded-lg shadow-lg shadow-red-200 transition-colors"
                                >
                                    Sí, Desactivar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CotizacionPaquete;
